import Decimal from "decimal.js";
import type { CostItemDraft, PricingItemDraft, ProjectDraft } from "./types";

Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

export function decimal(value: string | number | null | undefined): Decimal {
  if (value === null || value === undefined || value === "") return new Decimal(0);
  try {
    return new Decimal(value);
  } catch {
    return new Decimal(0);
  }
}

export function calculateCostItem(item: CostItemDraft): Decimal {
  if (item.manualAmountRmb !== null && item.manualAmountRmb !== undefined && item.manualAmountRmb !== "") {
    return decimal(item.manualAmountRmb);
  }

  const rawAmount = decimal(item.quantity).mul(decimal(item.unitPrice));
  if (item.currency.toUpperCase() === "CNY") return rawAmount;

  const fx = decimal(item.localCurrencyPerCny);
  return fx.gt(0) ? rawAmount.div(fx) : new Decimal(0);
}

export function calculatePricingItem(saleTons: string, item: PricingItemDraft): Decimal {
  const tons = decimal(saleTons);
  const price = decimal(item.benchmarkPriceRmb);
  const coefficient = decimal(item.settlementCoefficientPercent).div(100);
  if (item.basis === "fixed_per_ton") return tons.mul(price).mul(coefficient);
  if (item.basis === "grams_per_ton") return tons.mul(decimal(item.grade)).mul(price).mul(coefficient);
  return tons.mul(price).mul(decimal(item.grade).div(100)).mul(coefficient);
}

export function calculateProject(project: ProjectDraft) {
  const calculatedCosts = project.costs.map((item) => calculateCostItem(item));
  const additionalCost = calculatedCosts.reduce((sum, value) => sum.add(value), new Decimal(0));
  const dryTons = decimal(project.wetTons).mul(new Decimal(1).minus(decimal(project.moisturePercent).div(100)));
  const fobAmount = dryTons.mul(decimal(project.fobUnitPrice));
  const fobFx = decimal(project.fobCurrencyPerCny);
  const fobPurchaseCost = project.tradeTerm === "fob"
    ? (project.fobCurrency.toUpperCase() === "CNY" ? fobAmount : (fobFx.gt(0) ? fobAmount.div(fobFx) : new Decimal(0)))
    : new Decimal(0);
  const totalCost = additionalCost.add(fobPurchaseCost);
  const primaryRevenue = decimal(project.saleTons)
    .mul(decimal(project.benchmarkPriceRmb))
    .mul(decimal(project.gradePercent).div(100))
    .mul(decimal(project.settlementCoefficientPercent).div(100));
  const calculatedPricingItems = (project.pricingItems ?? []).map((item) => calculatePricingItem(project.saleTons, item));
  const otherPricingRevenue = calculatedPricingItems.reduce((sum, value) => sum.add(value), new Decimal(0));
  const formulaRevenue = primaryRevenue.add(otherPricingRevenue);

  const selectedRevenue = project.actualRevenueRmb
    ? decimal(project.actualRevenueRmb)
    : project.manualRevenueRmb
      ? decimal(project.manualRevenueRmb)
      : formulaRevenue;
  const profit = selectedRevenue.minus(totalCost);
  const margin = selectedRevenue.gt(0) ? profit.div(selectedRevenue).mul(100) : new Decimal(0);
  const startDate = project.paymentDate ? new Date(`${project.paymentDate}T00:00:00Z`) : null;
  const endRaw = project.actualCollectionDate || project.expectedCollectionDate;
  const endDate = endRaw ? new Date(`${endRaw}T00:00:00Z`) : null;
  const validDates = startDate && endDate && !Number.isNaN(startDate.valueOf()) && !Number.isNaN(endDate.valueOf()) && endDate >= startDate;
  const capitalDays = validDates ? Math.max(1, Math.ceil((endDate!.valueOf() - startDate!.valueOf()) / 86_400_000)) : null;
  const annualizedReturn = capitalDays && totalCost.gt(0) ? profit.div(totalCost).mul(365).div(capitalDays).mul(100) : null;

  return {
    calculatedCosts: calculatedCosts.map((value) => value.toDecimalPlaces(2).toFixed(2)),
    dryTons: dryTons.toDecimalPlaces(3).toFixed(3),
    fobPurchaseCost: fobPurchaseCost.toDecimalPlaces(2).toFixed(2),
    additionalCost: additionalCost.toDecimalPlaces(2).toFixed(2),
    totalCost: totalCost.toDecimalPlaces(2).toFixed(2),
    primaryRevenue: primaryRevenue.toDecimalPlaces(2).toFixed(2),
    calculatedPricingItems: calculatedPricingItems.map((value) => value.toDecimalPlaces(2).toFixed(2)),
    otherPricingRevenue: otherPricingRevenue.toDecimalPlaces(2).toFixed(2),
    formulaRevenue: formulaRevenue.toDecimalPlaces(2).toFixed(2),
    selectedRevenue: selectedRevenue.toDecimalPlaces(2).toFixed(2),
    profit: profit.toDecimalPlaces(2).toFixed(2),
    margin: margin.toDecimalPlaces(2).toFixed(2),
    capitalDays,
    annualizedReturn: annualizedReturn?.toDecimalPlaces(2).toFixed(2) ?? null,
  };
}

export function differencePercent(systemValue: string, manualValue?: string | null) {
  if (!manualValue) return new Decimal(0);
  const system = decimal(systemValue);
  const manual = decimal(manualValue);
  if (system.eq(0)) return manual.eq(0) ? new Decimal(0) : new Decimal(100);
  return manual.minus(system).abs().div(system.abs()).mul(100);
}
